import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signOut as amplifySignOut,
  signIn,
  confirmSignIn,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { isCognitoConfigured, isSSOConfigured } from './amplify';

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Load the current Cognito user as a { email, sub, name } object.
 * Tries fetchUserAttributes first; falls back to ID token claims for
 * OAuth/SSO flows where the access token lacks the admin scope.
 */
async function loadCognitoUser() {
  const { userId } = await getCurrentUser();

  try {
    const attributes = await fetchUserAttributes();
    return {
      email: attributes.email ?? '',
      sub: userId,
      name: attributes.name ?? attributes.email ?? '',
    };
  } catch {
    // OAuth/SSO flows — read claims from ID token instead
    const session = await fetchAuthSession();
    const claims = session.tokens?.idToken?.payload;
    return {
      email: claims?.email ?? '',
      sub: userId,
      name: claims?.name ?? claims?.email ?? '',
    };
  }
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check existing Cognito session on mount
  useEffect(() => {
    if (!isCognitoConfigured) {
      setIsLoading(false);
      return;
    }

    // If returning from an OAuth redirect (?code=&state= in URL),
    // skip the session check — Amplify will process the callback and
    // fire a Hub event which the listener below handles.
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') && params.has('state')) {
      return; // Let Hub listener handle OAuth callback
    }

    const checkSession = async () => {
      try {
        const u = await loadCognitoUser();
        setUser(u);
        setIsAuthenticated(true);
      } catch {
        // No valid session — user needs to log in
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  // Listen for OAuth redirect events (SSO callback)
  useEffect(() => {
    if (!isSSOConfigured) return;

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        try {
          const u = await loadCognitoUser();
          setUser(u);
          setIsAuthenticated(true);
          setError(null);
        } catch {
          setError('SSO sign-in succeeded but failed to load user profile.');
        } finally {
          setIsLoading(false);
          // Clean up OAuth params from URL
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
      if (payload.event === 'signInWithRedirect_failure') {
        setError('SSO sign-in failed. Please try again.');
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Clean up orphaned localStorage keys from old hand-rolled auth
  useEffect(() => {
    localStorage.removeItem('zdr_id_token');
    localStorage.removeItem('zdr_access_token');
    localStorage.removeItem('zdr_refresh_token');
  }, []);

  const loginWithSSO = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      await signInWithRedirect({ provider: { custom: 'AzureAD' } });
    } catch (err) {
      // If there's already a signed-in user, try to recover the session
      if (err instanceof Error && err.message.toLowerCase().includes('already')) {
        try {
          const u = await loadCognitoUser();
          setUser(u);
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        } catch {
          // Session is stale — sign out to clear it
          await amplifySignOut().catch(() => {});
          setError('Previous session was cleared. Please try SSO again.');
          setIsLoading(false);
          return;
        }
      }
      setError('Failed to start SSO sign-in.');
      setIsLoading(false);
    }
  }, []);

  const loginWithCredentials = useCallback(async (email, password) => {
    setError(null);
    setIsLoading(true);
    try {
      const result = await signIn({ username: email, password, options: { authFlowType: 'USER_PASSWORD_AUTH' } });

      // Handle first-login forced password change (admin-created users)
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        await confirmSignIn({ challengeResponse: password });
      }

      const u = await loadCognitoUser();
      setUser(u);
      setIsAuthenticated(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign-in failed.';
      setError(msg.includes('Incorrect') || msg.includes('NotAuthorizedException')
        ? 'Invalid email or password.'
        : msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isCognitoConfigured) {
        await amplifySignOut();
      }
      setUser(null);
      setIsAuthenticated(false);
    } catch {
      // Force clear state even if Amplify sign-out fails
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, error, loginWithSSO, loginWithCredentials, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
